import { useEffect, useRef, useState } from "react";
import { X, BookOpen, Plus, Search, ChevronDown, ChevronRight, LayoutGrid, Home } from "lucide-react";
import { userReportsApi } from "../../api/userReports";
import { LoadingText, Select } from "../ui";
import type { LibraryDashboard, LibraryPage, UserDashboard, UserWorkspace } from "../../types/reports";

interface Props {
  onClose: () => void;
  workspaces: UserWorkspace[];
  onDashboardAdded: (dashboard: UserDashboard) => void;
}

const PAGE_SIZE = 12;

export default function DashboardLibraryModal({ onClose, workspaces, onDashboardAdded }: Props) {
  const [page, setPage] = useState<LibraryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [knownOwners, setKnownOwners] = useState<string[]>([]);
  const [teamFilter, setTeamFilter] = useState("");
  const [knownTeams, setKnownTeams] = useState<string[]>([]);

  const [forkingId, setForkingId] = useState<string | null>(null);
  const [workspacePicker, setWorkspacePicker] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  function load(p: number, s: string, o: string, t: string) {
    setLoading(true);
    userReportsApi
      .listLibrary({ search: s, owner: o, team: t, page: p, page_size: PAGE_SIZE })
      .then((r) => {
        setPage(r.data);
        if (!s && !o && !t && p === 1) {
          const owners = Array.from(new Set(r.data.items.map((d) => d.owner_name))).sort();
          setKnownOwners(owners);
          const teams = Array.from(
            new Set(r.data.items.map((d) => d.owner_team).filter((x): x is string => !!x)),
          ).sort();
          setKnownTeams(teams);
        }
      })
      .finally(() => setLoading(false));
  }

  // Initial load
  useEffect(() => { load(1, "", "", ""); }, []);

  // Debounced search
  function handleSearchChange(val: string) {
    setSearch(val);
    setCurrentPage(1);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => load(1, val, ownerFilter, teamFilter), 300);
  }

  function handleOwnerChange(val: string) {
    setOwnerFilter(val);
    setCurrentPage(1);
    load(1, search, val, teamFilter);
  }

  function handleTeamChange(val: string) {
    setTeamFilter(val);
    setCurrentPage(1);
    load(1, search, ownerFilter, val);
  }

  function handlePageChange(p: number) {
    setCurrentPage(p);
    load(p, search, ownerFilter, teamFilter);
  }

  async function handleAdd(dashboardId: string, workspaceId?: string) {
    setForkingId(dashboardId);
    try {
      const res = await userReportsApi.forkLibraryDashboard(dashboardId, workspaceId);
      onDashboardAdded(res.data);
      onClose();
    } finally {
      setForkingId(null);
      setWorkspacePicker(null);
    }
  }

  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-[#1D345F]" />
            <h2 className="text-base font-semibold text-gray-900">Library</h2>
            {total > 0 && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">{total}</span>
            )}
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        {/* Search + filter bar */}
        <div className="flex gap-2 border-b border-gray-100 px-6 py-3 shrink-0">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search dashboards or widgets…"
              className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:border-[#1D345F] focus:outline-none"
            />
          </div>
          {knownTeams.length > 1 && (
            <Select value={teamFilter} onChange={(e) => handleTeamChange(e.target.value)} className="w-40">
              <option value="">All teams</option>
              {knownTeams.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          )}
          {knownOwners.length > 1 && (
            <Select value={ownerFilter} onChange={(e) => handleOwnerChange(e.target.value)} className="w-44">
              <option value="">All creators</option>
              {knownOwners.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </Select>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <LoadingText />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
              <BookOpen size={32} className="text-gray-300" />
              {search || ownerFilter || teamFilter ? (
                <>
                  <p className="text-sm text-gray-500">No dashboards match your search.</p>
                  <button
                    onClick={() => { setSearch(""); setOwnerFilter(""); setTeamFilter(""); load(1, "", "", ""); }}
                    className="text-xs text-blue-500 hover:underline"
                  >
                    Clear filters
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500">No dashboards have been shared to the library yet.</p>
                  <p className="text-xs text-gray-400">Share one of your dashboards using the toolbar menu.</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((d) => (
                <LibraryCard
                  key={d.id}
                  dashboard={d}
                  expanded={expandedId === d.id}
                  onToggleExpand={() => setExpandedId(expandedId === d.id ? null : d.id)}
                  workspaces={workspaces}
                  workspacePicker={workspacePicker}
                  onOpenWorkspacePicker={() => setWorkspacePicker(d.id)}
                  onCloseWorkspacePicker={() => setWorkspacePicker(null)}
                  forkingId={forkingId}
                  onAdd={handleAdd}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3 shrink-0">
            <p className="text-xs text-gray-400">
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                ‹ Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p as number)}
                      className={`rounded-lg border px-2.5 py-1 text-xs ${
                        currentPage === p
                          ? "border-[#1D345F] bg-[#1D345F] text-white"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Individual library card ─────────────────────────────────────────────────

interface LibraryCardProps {
  dashboard: LibraryDashboard;
  expanded: boolean;
  onToggleExpand: () => void;
  workspaces: UserWorkspace[];
  workspacePicker: string | null;
  onOpenWorkspacePicker: () => void;
  onCloseWorkspacePicker: () => void;
  forkingId: string | null;
  onAdd: (id: string, workspaceId?: string) => void;
}

function LibraryCard({
  dashboard: d,
  expanded,
  onToggleExpand,
  workspaces,
  workspacePicker,
  onOpenWorkspacePicker,
  onCloseWorkspacePicker,
  forkingId,
  onAdd,
}: LibraryCardProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Main row */}
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm">{d.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            By {d.owner_name} · {new Date(d.created_at).toLocaleDateString()}
          </p>
          {/* Expand toggle */}
          {d.tile_count > 0 && (
            <button
              onClick={onToggleExpand}
              className="mt-1.5 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <LayoutGrid size={11} />
              {d.tile_count} {d.tile_count === 1 ? "widget" : "widgets"}
            </button>
          )}
        </div>

        {/* Add button + destination menu */}
        <div className="relative shrink-0">
          <button
            onClick={() =>
              workspaces.length > 0
                ? (workspacePicker === d.id ? onCloseWorkspacePicker() : onOpenWorkspacePicker())
                : onAdd(d.id)
            }
            disabled={forkingId === d.id}
            className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#162847] disabled:opacity-50"
          >
            <Plus size={12} />
            {forkingId === d.id ? "Adding…" : "Add to my home"}
            {workspaces.length > 0 && (
              <ChevronDown size={12} className={`transition-transform ${workspacePicker === d.id ? "rotate-180" : ""}`} />
            )}
          </button>

          {workspacePicker === d.id && (
            <>
              <button
                type="button"
                aria-label="Close menu"
                onClick={onCloseWorkspacePicker}
                className="fixed inset-0 z-10 cursor-default"
              />
              <div className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Add to
                </p>
                <button
                  onClick={() => onAdd(d.id)}
                  disabled={forkingId === d.id}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Home size={14} className="shrink-0 text-gray-400" />
                  My home
                </button>
                {workspaces.map((ws) => (
                  <button
                    key={ws.id}
                    onClick={() => onAdd(d.id, ws.id)}
                    disabled={forkingId === d.id}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <LayoutGrid size={14} className="shrink-0 text-gray-400" />
                    <span className="truncate">{ws.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Expanded widget list */}
      {expanded && d.widget_titles.length > 0 && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Widgets included</p>
          <ul className="space-y-1">
            {d.widget_titles.map((title, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
                {title}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
